// Functions and variables in this file are intended to create strings
// out of specific objects and format them so they can be
// displayed in a human-readable form.

import { CREDITS_DISPLAY_NAME } from "@src/constants/credit-constants";
import { TEAM_DISPLAY_NAME, TEAMS_DISPLAY_NAME } from "@src/constants/team-constants";
import { UserRoleEnum } from "@custom-types/member-types";
/** Return readable text for company and project roles */
export function formatUserRoleType(userRole, options) {
  let customProjectRolePrefix;
  // We use project as default context for the project roles.
  switch (options?.projectContext ?? "project") {
    case "project":
    case "group":
    case "company":
      customProjectRolePrefix = "Project ";
      break;
    default:
      customProjectRolePrefix = "";
  }
  switch (userRole) {
    case "RESOURCE_ADMIN":
      return `${customProjectRolePrefix}Admin`;
    case "RESOURCE_EDITOR":
      return `${customProjectRolePrefix}Editor`;
    case "RESOURCE_VIEWER":
      return `${customProjectRolePrefix}Viewer`;
    case "COMPANY_EXECUTIVE":
      return "Admin";
    case "COMPANY_VIEWER":
      return "Viewer";
    case "COMPANY_MANAGER":
      return "Group Manager";
    case "GROUP_VIEWER":
      return "Group Viewer";
    case "RESOURCE_OWNER":
    case "PROJECT_MANAGER":
      return `${customProjectRolePrefix}Manager`;
    case "MEMBER":
      return "No workspace role";
  }
}

/**
 * A member's project role is inherited through a team when there is an effective
 * role but no direct (explicit) assignment on the project.
 */
export function isProjectAssignedThroughTeam(project) {
  return Boolean(project.role) && !project.explicitRole;
}

/**
 * Where a member's effective project role comes from:
 * - `direct`: the member's own assignment
 * - `team`: only a team the member belongs to, with no direct assignment
 * - `teamOverDirect`: a team grants a higher role than the direct assignment
 *
 * The payload does not say which team, and a team granting the same role as the
 * direct assignment is indistinguishable from `direct`.
 */

/** Returns where a member's effective project role comes from, or undefined without a role */
export function getProjectAccessSource(project) {
  if (!project.role) {
    return undefined;
  }
  if (!project.explicitRole) {
    return "team";
  }
  return UserRoleEnum[project.role] > UserRoleEnum[project.explicitRole] ? "teamOverDirect" : "direct";
}

/** Return the singular/plural term of a noun */
export const nounList = {
  /* eslint-disable @typescript-eslint/naming-convention */
  project: "projects",
  member: "members",
  annotation: "annotations",
  group: "groups",
  this: "these",
  role: "roles",
  "this user": "these users",
  Attachment: "Attachments",
  snapshot: "snapshots",
  day: "days",
  scan: "scans",
  cluster: "clusters",
  change: "changes",
  seat: "seats",
  [TEAM_DISPLAY_NAME]: TEAMS_DISPLAY_NAME,
  token: CREDITS_DISPLAY_NAME
  /* eslint-enable */
};
/** Return the singular/plural term of a noun with/without the actual counter */
export function nounPluralize({
  shouldShowCounter = true,
  counter,
  word
}) {
  const count = shouldShowCounter ? `${counter} ` : "";
  return counter === 1 ? count + word : count + nounList[word];
}

/** Return the verb in progressive format */
export const progressiveVerbList = {
  delete: "deleting",
  give: "giving",
  assign: "assigning",
  change: "changing",
  archive: "archiving",
  unarchive: "unarchiving",
  remove: "removing"
};
/** Return the progressive verb tense of a given verb */
export function progressiveVerb({
  verb,
  isProgressive
}) {
  return isProgressive ? progressiveVerbList[verb] : verb;
}

/** Return the formatted version of area unit */
export function formatAreaUnit(unit) {
  switch (unit) {
    case "sqft":
      return "Sq ft";
    case "sqm":
      return "Sq m";
  }
}

/** Returns user role description */
export function userRoleDescription(userRole) {
  switch (userRole) {
    case "COMPANY_EXECUTIVE":
      return "Complete control and access to the entire workspace and all settings.";
    case "COMPANY_VIEWER":
      return "Access to all projects of the workspace with viewing permission only.";
    case "COMPANY_MANAGER":
      return "Manages one or multiple groups of projects and project managers.";
    case "GROUP_VIEWER":
      return "Access one or multiple groups of projects with viewing permission only.";
    case "PROJECT_MANAGER":
      return "Manages one or multiple projects and access to them.";
    case "MEMBER":
      return "Access is restricted to individual projects.";
    case "RESOURCE_ADMIN":
      return "Can manage and edit individual projects they are invited to.";
    case "RESOURCE_EDITOR":
      return "Can edit invited projects but cannot access project properties, invite users, or change details and access levels.";
    case "RESOURCE_VIEWER":
      return "Can view individual active projects that they are invited to or have received a URL link for.";
    case "RESOURCE_OWNER":
      return "It is the owner of the project and can view and edit project details, invite other users, and change access levels.";
    default:
      return "";
  }
}

/**
 * Returns a string representation of a number using locale-specific formatting for prices.
 * For example:
 * - 1500 -> "1,500" (en-US)
 * - 1500 -> "1.500" (de-DE)
 * @param price The numeric amount to format.
 * @param selectedLocales The locale string to determine formatting style (e.g. "en-US").
 * @returns The formatted price with proper thousand separators and decimal points
 */
export function formatPrice(price, selectedLocales) {
  return price.toLocaleString(selectedLocales);
}

/**
 * Returns a string representation of a number formatted with space separation
 * * For example:
 * - 1500 -> "1 500"
 * - 32000 -> "32 000"
 * @param number The numeric value to format
 * @returns The formatted number with thousand separation by space
 */
export function formatNumber(value) {
  // English number format comma separated value, replacing the commas
  // with space provides us the space separated value
  return value.toLocaleString("en-En").replaceAll(",", " ");
}